import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { resolveCargosColaborador } from '../../utils/colaboradorCargo';
import { formatarDataReuniaoCurta } from '../../utils/reuniaoDate';
import './GerenciarPresencas.css';

function formatHoraReuniao(reuniao) {
  const hi = reuniao?.horaInicio ?? reuniao?.hora_inicio;
  const hf = reuniao?.horaFim ?? reuniao?.hora_fim;
  if (!hi) return '';
  const fmt = (timeValue) => {
    if (!timeValue) return '--:--';
    if (typeof timeValue === 'string' && timeValue.includes('T')) {
      return new Date(timeValue).toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
      });
    }
    return String(timeValue).substring(0, 5);
  };
  return `${fmt(hi)}${hf ? ` – ${fmt(hf)}` : ''}`;
}

/**
 * @param {object} props
 * @param {string} props.reuniaoId
 * @param {boolean} [props.embedded] — quando true, omite cabeçalho duplicado da reunião
 * @param {() => void} [props.onSalvo]
 */
const PresencasReuniaoPanel = ({ reuniaoId, embedded = false, onSalvo }) => {
  const navigate = useNavigate();
  const [reuniao, setReuniao] = useState(null);
  const [participantes, setParticipantes] = useState([]);
  const [presencas, setPresencas] = useState({});
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  /** Após confirmar (salvar) ou se a reunião já tiver presença de todos os participantes. */
  const [formularioBloqueado, setFormularioBloqueado] = useState(false);

  const carregarDados = useCallback(async () => {
    try {
      setLoading(true);

      const [reuniaoResponse, presencasResponse] = await Promise.all([
        api.get(`/reunioes/${reuniaoId}/participantes`),
        api.get(`/presencas/reuniao/${reuniaoId}`),
      ]);

      const reuniaoData = reuniaoResponse.data?.data ?? reuniaoResponse.data;
      setReuniao(reuniaoData);

      let listaParticipantes = (reuniaoData?.reuniaoParticipantes || []).map((rp) => {
        const c = rp.colaborador || {};
        const { cargo_id, cargo_nome, cargo_ids } = resolveCargosColaborador(c);
        return {
          id: c.id,
          nome: c.nomeCompleto ?? c.nome_completo,
          cargo_id,
          cargo_nome,
          cargo_ids,
          telefone: c.telefone,
          email: c.email,
        };
      });

      if (listaParticipantes.length === 0) {
        const colabRes = await api.get('/colaboradores');
        const lista = colabRes.data?.data || colabRes.data || [];
        listaParticipantes = lista.map((c) => {
          const { cargo_id, cargo_nome, cargo_ids } = resolveCargosColaborador(c);
          return {
            id: c.id,
            nome: c.nomeCompleto || c.nome_completo,
            cargo_id,
            cargo_nome,
            cargo_ids,
            telefone: c.telefone,
            email: c.email,
          };
        });
      }

      setParticipantes(listaParticipantes);

      const presencasExistentes = {};
      (presencasResponse.data?.data || []).forEach((presenca) => {
        const cid = presenca.colaboradorId ?? presenca.colaborador_id;
        presencasExistentes[cid] = {
          presente: presenca.presente,
          observacoes: presenca.observacoes || '',
        };
      });

      setPresencas(presencasExistentes);

      const todasJaRegistradas =
        listaParticipantes.length > 0 &&
        listaParticipantes.every((p) => Object.prototype.hasOwnProperty.call(presencasExistentes, p.id));
      setFormularioBloqueado(todasJaRegistradas);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados da reunião');
    } finally {
      setLoading(false);
    }
  }, [reuniaoId]);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  const handlePresencaChange = (colaboradorId, presente) => {
    if (formularioBloqueado) return;
    setPresencas((prev) => ({
      ...prev,
      [colaboradorId]: {
        ...prev[colaboradorId],
        presente,
      },
    }));
  };

  const handleObservacaoChange = (colaboradorId, observacoes) => {
    if (formularioBloqueado) return;
    setPresencas((prev) => ({
      ...prev,
      [colaboradorId]: {
        ...prev[colaboradorId],
        observacoes,
      },
    }));
  };

  const marcarTodosPresentes = () => {
    if (formularioBloqueado) return;
    const novasPresencas = {};
    participantes.forEach((participante) => {
      novasPresencas[participante.id] = {
        presente: true,
        observacoes: presencas[participante.id]?.observacoes || '',
      };
    });
    setPresencas(novasPresencas);
  };

  const marcarTodosAusentes = () => {
    if (formularioBloqueado) return;
    const novasPresencas = {};
    participantes.forEach((participante) => {
      novasPresencas[participante.id] = {
        presente: false,
        observacoes: presencas[participante.id]?.observacoes || '',
      };
    });
    setPresencas(novasPresencas);
  };

  const marcarPorCargo = (cargoId, presente) => {
    if (formularioBloqueado || cargoId == null) return;
    const novasPresencas = { ...presencas };
    participantes
      .filter((p) => {
        if (Array.isArray(p.cargo_ids) && p.cargo_ids.length) {
          return p.cargo_ids.includes(cargoId);
        }
        return p.cargo_id === cargoId;
      })
      .forEach((participante) => {
        novasPresencas[participante.id] = {
          ...novasPresencas[participante.id],
          presente,
        };
      });
    setPresencas(novasPresencas);
  };

  const salvarPresencas = async () => {
    if (formularioBloqueado) return;
    try {
      setSalvando(true);

      const dadosPresencas = participantes.map((participante) => ({
        reuniao_id: parseInt(reuniaoId, 10),
        colaborador_id: participante.id,
        presente: presencas[participante.id]?.presente || false,
        observacoes: presencas[participante.id]?.observacoes || '',
      }));

      await api.post('/presencas/lote', { presencas: dadosPresencas });

      setFormularioBloqueado(true);
      toast.success('Presenças confirmadas. O registro foi bloqueado para alterações.');
      onSalvo?.();
    } catch (error) {
      console.error('Erro ao salvar presenças:', error);
      toast.error('Erro ao salvar presenças');
    } finally {
      setSalvando(false);
    }
  };

  const agruparPorCargo = () => {
    const grupos = {};

    participantes.forEach((participante) => {
      const nomeComVirgula =
        participante.cargo_nome &&
        (participante.cargo_nome.includes(',') || participante.cargo_nome.includes(', '));

      if (nomeComVirgula) {
        const cargosNomes = participante.cargo_nome
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        const cargosIds = Array.isArray(participante.cargo_ids)
          ? participante.cargo_ids
          : typeof participante.cargo_ids === 'string'
            ? participante.cargo_ids
                .split(',')
                .map((id) => parseInt(id, 10))
                .filter((n) => !Number.isNaN(n))
            : [];

        cargosNomes.forEach((cargoNome, index) => {
          const cargoId = cargosIds[index] ?? null;
          if (!grupos[cargoNome]) {
            grupos[cargoNome] = {
              cargo_id: cargoId,
              participantes: [],
            };
          }
          if (!grupos[cargoNome].participantes.find((p) => p.id === participante.id)) {
            grupos[cargoNome].participantes.push(participante);
          }
        });
      } else {
        const cargoNome = participante.cargo_nome || 'Sem Cargo';
        if (!grupos[cargoNome]) {
          grupos[cargoNome] = {
            cargo_id: participante.cargo_id,
            participantes: [],
          };
        }
        grupos[cargoNome].participantes.push(participante);
      }
    });

    return grupos;
  };

  const contarPresencas = () => {
    const total = participantes.length;
    const presentes = Object.values(presencas).filter((p) => p.presente).length;
    const registrados = Object.keys(presencas).length;
    return { total, presentes, ausentes: total - presentes, registrados };
  };

  const PresencaDonut = ({ total, presentes, ausentes }) => {
    const pct = total > 0 ? Math.round((presentes / total) * 100) : 0;
    const R = 44;
    const circunferencia = 2 * Math.PI * R;
    const dashPresente = total > 0 ? (presentes / total) * circunferencia : 0;
    const dashAusente = total > 0 ? (ausentes / total) * circunferencia : 0;
    const offset = circunferencia * 0.25; // começa no topo

    return (
      <div className="presenca-chart-wrap">
        {/* Donut */}
        <div className="presenca-donut-box">
          <svg viewBox="0 0 100 100" className="presenca-donut-svg" aria-hidden="true">
            {/* Trilha */}
            <circle cx="50" cy="50" r={R} fill="none" stroke="#e4e4e7" strokeWidth="10" />
            {/* Ausentes (vermelho) */}
            {ausentes > 0 && (
              <circle
                cx="50" cy="50" r={R} fill="none"
                stroke="#f87171"
                strokeWidth="10"
                strokeDasharray={`${dashAusente} ${circunferencia - dashAusente}`}
                strokeDashoffset={-(dashPresente - offset)}
                strokeLinecap="round"
              />
            )}
            {/* Presentes (verde-escuro) */}
            {presentes > 0 && (
              <circle
                cx="50" cy="50" r={R} fill="none"
                stroke="#22c55e"
                strokeWidth="10"
                strokeDasharray={`${dashPresente} ${circunferencia - dashPresente}`}
                strokeDashoffset={offset}
                strokeLinecap="round"
              />
            )}
          </svg>
          <div className="presenca-donut-center">
            <span className="presenca-donut-pct">{pct}%</span>
            <span className="presenca-donut-label">presença</span>
          </div>
        </div>

        {/* Legenda + barra */}
        <div className="presenca-chart-info">
          <div className="presenca-barra-wrap">
            <div
              className="presenca-barra-fill presenca-barra-presente"
              style={{ width: total > 0 ? `${(presentes / total) * 100}%` : '0%' }}
            />
            <div
              className="presenca-barra-fill presenca-barra-ausente"
              style={{ width: total > 0 ? `${(ausentes / total) * 100}%` : '0%' }}
            />
          </div>

          <div className="presenca-legenda">
            <div className="presenca-legenda-item">
              <span className="presenca-legenda-dot dot-presente" />
              <span className="presenca-legenda-texto">
                <strong>{presentes}</strong> Presentes
              </span>
            </div>
            <div className="presenca-legenda-item">
              <span className="presenca-legenda-dot dot-ausente" />
              <span className="presenca-legenda-texto">
                <strong>{ausentes}</strong> Ausentes
              </span>
            </div>
            <div className="presenca-legenda-item presenca-legenda-total">
              <span className="presenca-legenda-texto muted">
                {total} no total
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={embedded ? 'presencas-panel-embedded' : 'presencas-container'}>
        <div className="loading">Carregando presenças...</div>
      </div>
    );
  }

  if (!reuniao) {
    return (
      <div className={embedded ? 'presencas-panel-embedded' : 'presencas-container'}>
        <div className="error">Reunião não encontrada</div>
      </div>
    );
  }

  const gruposPorCargo = agruparPorCargo();
  const { total, presentes, ausentes } = contarPresencas();
  const dataStr = formatarDataReuniaoCurta(reuniao.data ?? reuniao.dataReuniao);
  const horaStr = formatHoraReuniao(reuniao);

  const containerClass = embedded ? 'presencas-panel-embedded' : 'presencas-container';
  const bloqueadoClass = formularioBloqueado ? ' formulario-bloqueado' : '';

  return (
    <div className={`${containerClass}${bloqueadoClass}`}>
      {formularioBloqueado && (
        <div className="presencas-bloqueio-aviso" role="status">
          Presenças já confirmadas. O formulário está bloqueado e não pode ser alterado.
        </div>
      )}
      {!embedded && (
        <div className="presencas-header">
          <h1>Gerenciar Presenças</h1>
          <div className="reuniao-info">
            <h2>{reuniao.titulo}</h2>
            <p>
              {dataStr}
              {horaStr ? ` às ${horaStr}` : ''}
            </p>
            <p>{reuniao.local}</p>
          </div>
        </div>
      )}

      <div className="presencas-stats">
        <div className="stat-card">
          <span className="stat-number">{total}</span>
          <span className="stat-label">Total</span>
        </div>
        <div className="stat-card present">
          <span className="stat-number">{presentes}</span>
          <span className="stat-label">Presentes</span>
        </div>
        <div className="stat-card absent">
          <span className="stat-number">{ausentes}</span>
          <span className="stat-label">Ausentes</span>
        </div>
      </div>

      {/* Gráfico visual de frequência */}
      {total > 0 && (
        <PresencaDonut total={total} presentes={presentes} ausentes={ausentes} />
      )}

      {/* Lista de ausentes em destaque */}
      {(() => {
        const ausentesLista = participantes.filter(
          (p) => presencas[p.id]?.presente === false
        );
        if (ausentesLista.length === 0) return null;
        return (
          <div className="ausentes-destaque">
            <div className="ausentes-destaque-header">
              <span className="ausentes-destaque-icone" aria-hidden="true">!</span>
              <div>
                <h4 className="ausentes-destaque-titulo">
                  {ausentesLista.length}{' '}
                  {ausentesLista.length === 1 ? 'ausente' : 'ausentes'}
                </h4>
                <p className="ausentes-destaque-sub">
                  Quem não compareceu nesta reunião
                </p>
              </div>
            </div>
            <div className="ausentes-destaque-chips">
              {ausentesLista.map((p) => (
                <span key={p.id} className="ausentes-destaque-chip">
                  {p.nome}
                </span>
              ))}
            </div>
          </div>
        );
      })()}

      <div className="presencas-actions">
        <button
          type="button"
          className="btn btn-success"
          onClick={marcarTodosPresentes}
          disabled={formularioBloqueado}
        >
          Marcar Todos Presentes
        </button>
        <button
          type="button"
          className="btn btn-danger"
          onClick={marcarTodosAusentes}
          disabled={formularioBloqueado}
        >
          Marcar Todos Ausentes
        </button>
      </div>

      <div className="presencas-content">
        {Object.entries(gruposPorCargo).map(([cargoNome, grupo]) => (
          <div key={cargoNome} className="cargo-group">
            <div className="cargo-header">
              <h3>{cargoNome}</h3>
              <div className="cargo-actions">
                <button
                  type="button"
                  className="btn btn-sm btn-success"
                  onClick={() => marcarPorCargo(grupo.cargo_id, true)}
                  disabled={formularioBloqueado}
                >
                  Todos Presentes
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  onClick={() => marcarPorCargo(grupo.cargo_id, false)}
                  disabled={formularioBloqueado}
                >
                  Todos Ausentes
                </button>
              </div>
            </div>

            <div className="participantes-list">
              {grupo.participantes.map((participante) => (
                <div key={participante.id} className="participante-item">
                  <div className="participante-info">
                    <span className="participante-nome">
                      {participante.nome || participante.nome_completo}
                    </span>
                    <span className="participante-email">{participante.email}</span>
                  </div>

                  <div className="presenca-controls">
                    <div className="presenca-radio">
                      <label>
                        <input
                          type="radio"
                          name={`presenca_${participante.id}`}
                          checked={presencas[participante.id]?.presente === true}
                          onChange={() => handlePresencaChange(participante.id, true)}
                          disabled={formularioBloqueado}
                        />
                        Presente
                      </label>
                      <label>
                        <input
                          type="radio"
                          name={`presenca_${participante.id}`}
                          checked={presencas[participante.id]?.presente === false}
                          onChange={() => handlePresencaChange(participante.id, false)}
                          disabled={formularioBloqueado}
                        />
                        Ausente
                      </label>
                    </div>

                    <input
                      type="text"
                      placeholder="Observações"
                      value={presencas[participante.id]?.observacoes || ''}
                      onChange={(e) => handleObservacaoChange(participante.id, e.target.value)}
                      className="observacoes-input"
                      disabled={formularioBloqueado}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="form-actions">
        {!embedded && (
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/presencas')}>
            Cancelar
          </button>
        )}
        <button
          type="button"
          className="btn btn-primary"
          onClick={salvarPresencas}
          disabled={salvando || formularioBloqueado}
        >
          {formularioBloqueado ? 'Presenças confirmadas' : salvando ? 'Salvando...' : 'Salvar Presenças'}
        </button>
      </div>
    </div>
  );
};

export default PresencasReuniaoPanel;
